import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode, ReactElement } from 'react';
import { apiGet } from '../api/client';
import { ENDPOINTS } from '../api/endpoints';
import type { AuthConfig } from '../api/types';
import { setToken, clearAllTokens, isAuthenticated as checkAuth } from './token-store';
import { signIn as cognitoSignIn, signUp as cognitoSignUp, confirmSignUp as cognitoConfirm, resendConfirmationCode, forgotPassword as cognitoForgot, resetPassword as cognitoReset } from './cognito';
import type { CognitoConfig } from './cognito';

type AuthState = {
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly config: AuthConfig | null;
  readonly pendingEmail: string | null;
};

type AuthActions = {
  readonly signIn: (email: string, password: string) => Promise<{ challengeName?: string }>;
  readonly signUp: (email: string, password: string, tosVersion: string) => Promise<void>;
  readonly confirm: (code: string) => Promise<void>;
  readonly resendCode: () => Promise<void>;
  readonly forgot: (email: string) => Promise<void>;
  readonly reset: (code: string, newPassword: string) => Promise<void>;
  readonly signOut: () => void;
  readonly setPendingEmail: (email: string) => void;
};

type AuthContextValue = AuthState & AuthActions;

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
  readonly children: ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps): ReactElement => {
  const [isAuth, setIsAuth] = useState(checkAuth());
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  useEffect(() => {
    const loadConfig = async (): Promise<void> => {
      try {
        const authConfig = await apiGet<AuthConfig>(ENDPOINTS.authConfig);
        setConfig(authConfig);
      } catch {
        // Auth config unavailable - auth features disabled
      } finally {
        setIsLoading(false);
      }
    };
    void loadConfig();
  }, []);

  const getCognitoConfig = useCallback((): CognitoConfig => {
    if (!config) throw new Error('Auth config not loaded');
    return { cognitoEndpoint: config.cognitoEndpoint, clientId: config.clientId };
  }, [config]);

  const signIn = useCallback(async (email: string, password: string): Promise<{ challengeName?: string }> => {
    const result = await cognitoSignIn(getCognitoConfig(), email, password);
    if (result.AuthenticationResult) {
      setToken('access_token', result.AuthenticationResult.AccessToken);
      setToken('id_token', result.AuthenticationResult.IdToken);
      if (result.AuthenticationResult.RefreshToken) {
        setToken('refresh_token', result.AuthenticationResult.RefreshToken);
      }
      setIsAuth(true);
      return {};
    }
    return { challengeName: result.ChallengeName };
  }, [getCognitoConfig]);

  const signUp = useCallback(async (email: string, password: string, tosVersion: string): Promise<void> => {
    await cognitoSignUp(getCognitoConfig(), email, password, tosVersion);
    setPendingEmail(email);
  }, [getCognitoConfig]);

  const confirm = useCallback(async (code: string): Promise<void> => {
    if (!pendingEmail) throw new Error('No pending email for confirmation');
    await cognitoConfirm(getCognitoConfig(), pendingEmail, code);
  }, [getCognitoConfig, pendingEmail]);

  const resendCode = useCallback(async (): Promise<void> => {
    if (!pendingEmail) throw new Error('No pending email');
    await resendConfirmationCode(getCognitoConfig(), pendingEmail);
  }, [getCognitoConfig, pendingEmail]);

  const forgot = useCallback(async (email: string): Promise<void> => {
    await cognitoForgot(getCognitoConfig(), email);
    setPendingEmail(email);
  }, [getCognitoConfig]);

  const reset = useCallback(async (code: string, newPassword: string): Promise<void> => {
    if (!pendingEmail) throw new Error('No pending email for reset');
    await cognitoReset(getCognitoConfig(), pendingEmail, code, newPassword);
  }, [getCognitoConfig, pendingEmail]);

  const signOut = useCallback((): void => {
    clearAllTokens();
    setIsAuth(false);
  }, []);

  const value = useMemo((): AuthContextValue => ({
    isAuthenticated: isAuth,
    isLoading,
    config,
    pendingEmail,
    signIn,
    signUp,
    confirm,
    resendCode,
    forgot,
    reset,
    signOut,
    setPendingEmail,
  }), [isAuth, isLoading, config, pendingEmail, signIn, signUp, confirm, resendCode, forgot, reset, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// useAuth is tightly coupled to AuthContext (defined above) and is imported by name from this
// module across the app. Splitting it out solely to satisfy react-refresh's fast-refresh DX hint
// would force a re-export (banned) or churn every call site for no runtime benefit.
// eslint-disable-next-line react-refresh/only-export-components
export { AuthProvider, useAuth };
